/**
 * ChatKitWidget Component
 * 
 * Widget flutuante com ChatKit oficial da OpenAI
 * Integração com Workflow usando @openai/chatkit-react
 * 
 * ✨ CONTEXT-AWARE: Captura automaticamente o estado da página atual
 * e envia para o agente via metadata.pageContext
 * 
 * Docs: https://platform.openai.com/docs/guides/chatkit
 */

import { useState, useEffect } from 'react';
import {
  Fab,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useTheme,
  Box,
} from '@mui/material';
import { Chat as ChatIcon, Close as CloseIcon } from '@mui/icons-material';
import { ChatKit, useChatKit } from '@openai/chatkit-react';
import { useAgentContext } from '../../hooks/useAgentContext';

// Gera ou recupera um deviceId único para o usuário
function getDeviceId(): string {
  const key = 'chatkit_device_id';
  let deviceId = localStorage.getItem(key);
  
  if (!deviceId) {
    deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(key, deviceId);
  }
  
  return deviceId;
}

export function ChatKitWidget() {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [deviceId, setDeviceId] = useState<string>('');

  // ✨ Hook que captura contexto da página atual (Dashboard, Premissas, DRE)
  const pageContext = useAgentContext();

  useEffect(() => {
    // Inicializa o deviceId quando o componente monta
    setDeviceId(getDeviceId());
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // Hook do ChatKit para gerenciar a sessão
  const { control } = useChatKit({
    api: {
      async getClientSecret(existing) {
        // Se já temos um client_secret válido, podemos tentar reutilizá-lo
        if (existing) {
          console.log('[ChatKit] Reusing existing client secret');
          // Implementar lógica de refresh se necessário
        }

        try {
          console.log('[ChatKit] Creating new session with context:', {
            page: pageContext.page,
            scenario: pageContext.currentScenario,
            cardsCount: pageContext.cards?.length || 0,
            timestamp: pageContext.timestamp,
          });

          // Chama nosso endpoint serverless COM pageContext
          const response = await fetch('/api/chatkit/session', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              deviceId: deviceId,
              pageContext: pageContext, // ✅ Envia contexto da página
            }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to create ChatKit session');
          }

          const data = await response.json();
          console.log('[ChatKit] Session created successfully');
          
          // O contexto é retornado junto com client_secret mas será usado via system prompt
          if (data.pageContext) {
            console.log('[ChatKit] Context available:', {
              page: data.pageContext.page,
              scenario: data.pageContext.currentScenario,
              cardsCount: data.pageContext.cards?.length || 0
            });
            console.log('[ChatKit] Configure Agent Builder system prompt to request/use this context');
          }
          
          return data.client_secret;
        } catch (error) {
          console.error('[ChatKit] Error getting client secret:', error);
          throw error;
        }
      },
    },
  });

  return (
    <>
      {/* Botão flutuante */}
      <Fab
        color="primary"
        aria-label="Abrir assistente AI"
        onClick={handleToggle}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1300,
          boxShadow: theme.shadows[8],
          '&:hover': {
            boxShadow: theme.shadows[12],
          },
        }}
      >
        <ChatIcon />
      </Fab>

      {/* Diálogo do chat */}
      <Dialog
        open={isOpen}
        onClose={handleToggle}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            height: { xs: '90vh', sm: '600px', md: '700px' },
            maxHeight: '90vh',
            m: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            p: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChatIcon />
            <Box>
              <Box sx={{ fontWeight: 600, fontSize: '1rem' }}>
                Assistente Valuation
              </Box>
              <Box sx={{ fontSize: '0.75rem', opacity: 0.9 }}>
                Tire suas dúvidas sobre valuation
              </Box>
            </Box>
          </Box>
          <IconButton
            onClick={handleToggle}
            size="small"
            sx={{ color: 'inherit' }}
            aria-label="Fechar chat"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {/* ChatKit oficial da OpenAI */}
          {deviceId && (
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <ChatKit 
                control={control}
                style={{ 
                  height: '100%', 
                  width: '100%',
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
